import React, { useRef, useState } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { weddingData } from '../data';

export const TimelineSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [activeIndex, setActiveIndex] = useState(0);
  const activeEvent = weddingData.timeline[activeIndex];
  const tabsRef = useRef(null);

  const handleTabClick = (index) => {
    setActiveIndex(index);
    const container = tabsRef.current;
    if (!container) return;
    const tab = container.children[index];
    if (!tab) return;
    const containerPadding = 24; // px-6 = 24px
    const scrollLeft = tab.offsetLeft - container.offsetWidth / 2 + tab.offsetWidth / 2 - containerPadding;
    container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
  };

  return (
    <section ref={ref} className="py-20 md:py-40 px-6 bg-stone-50" data-testid="timeline-section">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="text-center mb-10 md:mb-20"
        >
          <div className="flex items-center justify-center gap-4 mb-6 md:mb-8">
            <div className="h-px w-12 bg-stone-300"></div>
            <p className="text-xs uppercase tracking-widest text-stone-400">The Celebration</p>
            <div className="h-px w-12 bg-stone-300"></div>
          </div>
          <h2 className="font-elegant text-4xl md:text-6xl lg:text-7xl text-stone-900">
            Schedule
          </h2>
        </motion.div>

        {/* ========== MOBILE: Swipeable Cards ========== */}
        <div className="md:hidden">
          <style>{`[data-testid="timeline-section"] .snap-x::-webkit-scrollbar { display: none; }`}</style>
          <div
            ref={tabsRef}
            className="flex gap-4 overflow-x-auto -mx-6 px-6 pb-2 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
            onScroll={(e) => {
              const container = e.target;
              const firstCard = container.querySelector('[data-card]');
              if (!firstCard) return;
              const cardWidth = firstCard.offsetWidth;
              const gap = 16;
              const idx = Math.round(container.scrollLeft / (cardWidth + gap));
              const clamped = Math.max(0, Math.min(idx, weddingData.timeline.length - 1));
              if (clamped !== activeIndex) setActiveIndex(clamped);
            }}
          >
            {weddingData.timeline.map((event) => (
              <div
                key={event.id}
                data-card
                className="flex-shrink-0 w-[85vw] snap-center bg-white border border-stone-200 overflow-hidden flex flex-col"
                data-testid={`timeline-card-mobile-${event.id}`}
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="space-y-3">
                    <h3 className="font-elegant text-2xl text-stone-900">{event.title}</h3>
                    <p className="text-sm text-stone-500 font-light leading-relaxed">{event.description}</p>
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">When</p>
                        <p className="text-sm text-stone-700">{event.date}</p>
                        <p className="text-base font-elegant text-stone-900">{event.time}</p>
                      </div>
                      {event.attire && (
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Attire</p>
                          <p className="text-sm text-stone-600 font-light leading-relaxed">{event.attire}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="pt-4 border-t border-stone-100 mt-auto">
                    <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Venue</p>
                    <p className="text-xs font-medium text-stone-800 uppercase tracking-wide">{event.venue}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dots indicator */}
          <div className="flex items-center justify-center gap-2 mt-5">
            {weddingData.timeline.map((_, index) => (
              <div
                key={index}
                className={`rounded-full transition-all duration-300 ${
                  activeIndex === index ? 'w-6 h-2 bg-stone-800' : 'w-2 h-2 bg-stone-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* ========== DESKTOP: Panel + Dots Timeline ========== */}
        <div className="hidden md:block">
          {/* Content Panel */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            className="bg-white border border-stone-200 p-10 mb-16 min-h-[320px]"
            data-testid="timeline-content-panel"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className="flex gap-10"
              >
                {/* Image */}
                <div className="w-64 lg:w-72 flex-shrink-0">
                  <div className="aspect-square overflow-hidden bg-stone-100">
                    <img src={activeEvent.image} alt={activeEvent.title} className="w-full h-full object-cover" />
                  </div>
                </div>

                {/* Title + Description */}
                <div className="flex-1 space-y-4">
                  <h3 className="font-elegant text-4xl lg:text-5xl text-stone-900 leading-tight">{activeEvent.title}</h3>
                  <p className="text-base text-stone-500 font-light leading-relaxed">{activeEvent.description}</p>
                </div>

                {/* Venue & Time & Attire */}
                <div className="w-52 lg:w-60 flex-shrink-0 border-l border-stone-200 pl-8 space-y-5">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-stone-400 mb-2">Venue</p>
                    <p className="text-sm font-medium text-stone-800 uppercase tracking-wide leading-snug">{activeEvent.venue}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-stone-400 mb-2">When</p>
                    <p className="text-sm text-stone-700">{activeEvent.date}</p>
                    <p className="text-lg font-elegant text-stone-900">{activeEvent.time}</p>
                  </div>
                  {activeEvent.attire && (
                    <div>
                      <p className="text-xs uppercase tracking-widest text-stone-400 mb-2">Attire</p>
                      <p className="text-sm text-stone-600 font-light leading-relaxed">{activeEvent.attire}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* Timeline Bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
          >
            <div className="relative flex items-start justify-between">
              <div className="absolute left-0 right-0 top-[9px] h-px bg-stone-300"></div>
              <motion.div
                className="absolute left-0 top-[9px] h-px bg-stone-800 origin-left"
                animate={{ width: `${(activeIndex / (weddingData.timeline.length - 1)) * 100}%` }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              ></motion.div>

              {weddingData.timeline.map((event, index) => (
                <button
                  key={event.id}
                  onClick={() => setActiveIndex(index)}
                  data-testid={`timeline-dot-${event.id}`}
                  className={`flex flex-col items-center cursor-pointer transition-colors duration-300 w-0 flex-1 ${
                    activeIndex === index ? 'text-stone-900' : 'text-stone-400'
                  }`}
                >
                  <motion.div
                    animate={{
                      width: activeIndex === index ? 18 : 12,
                      height: activeIndex === index ? 18 : 12,
                      backgroundColor: index <= activeIndex ? '#292524' : '#d6d3d1',
                    }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="rounded-full border-4 border-stone-50 shadow-sm hover:scale-125 transition-transform z-10"
                  ></motion.div>
                  <div className="mt-4 text-center">
                    <p className={`text-xs md:text-sm uppercase tracking-wider leading-tight mb-1 ${
                      activeIndex === index ? 'font-medium' : 'font-light'
                    }`}>{event.title}</p>
                    <p className="text-[10px] md:text-xs tracking-wide">{event.date}</p>
                    <p className="text-[10px] md:text-xs tracking-wide">{event.time}</p>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
